/**
 * Extraction Response Parser - 2-Step API
 *
 * Parses the assessment and player data from the 2-step extraction API
 * and converts them to ParsedCSV format for field-mapping.
 *
 * Ported from mobile/utils/extraction-response-parser.ts for full feature parity.
 */

import { parseStructureAnalysis, parseRawPlayerData } from './pipe-parser';
import type { ParsedCSV, ExtractedPlayerRow, AssessResponse, ExtractResponse } from '../types/extraction';
import type { ExtractedRow } from "../types/extraction";

// =============================================================================
// Main Parser Function
// =============================================================================

/**
 * Parse the 2-step extraction response into ParsedCSV format
 *
 * @param assessResponse Assessment response from assess endpoint
 * @param extractResponse Extract response from extract endpoint
 * @returns ParsedCSV for field-mapping screen
 */
export function parseExtractionData(
  assessResponse: AssessResponse,
  extractResponse: ExtractResponse
): ParsedCSV {
  // Step 1: Parse structure analysis (always needed for roster_type, teams, etc.)
  const structure = parseStructureAnalysis(assessResponse.assessment);

  // Step 2: Use pass2 columns if available (authoritative, post-merge), fall back to pass1
  const pass2Columns = extractResponse.columns;
  const columns = pass2Columns && pass2Columns.length > 0 ? pass2Columns : structure.columns;
  console.log(`[ResponseParser] Using columns from: ${pass2Columns && pass2Columns.length > 0 ? 'API (pass2)' : 'assessment (pass1)'}`);

  // Step 3: Parse player data using the selected columns
  const playerRows = parseRawPlayerData(extractResponse.players, columns, structure);

  // Step 4: Convert to CSV format for field-mapping
  const parsedCSV = buildParsedCSV(playerRows, structure.roster_type);

  return parsedCSV;
}

// =============================================================================
// CSV Building
// =============================================================================

/**
 * Parse a semicolon-delimited CSV string into key-value pairs
 * Format: "key1:value1;key2:value2" -> { key1: "value1", key2: "value2" }
 */
function parseSemicolonCsv(csv: string): Record<string, string> {
  if (!csv || csv === '-') return {};
  const result: Record<string, string> = {};
  const pairs = csv.split(';');
  for (const pair of pairs) {
    const colonIndex = pair.indexOf(':');
    if (colonIndex > 0) {
      const key = pair.substring(0, colonIndex).trim();
      const value = pair.substring(colonIndex + 1).trim();
      if (key && value) {
        result[key] = value;
      }
    }
  }
  return result;
}

/**
 * Expanded player data with unpacked stats and contact info
 */
interface ExpandedPlayerData {
  // Direct fields from ExtractedPlayerRow
  roster_type: string;
  team_id: string;
  team_color: string;
  number: string;
  name_last: string;
  name_first: string;
  position: string;
  bats: string;
  throws: string;
  /**
   * The Class cell as the roster wrote it — "RS Fr.", not "2030".
   *
   * `pipe-parser` derives `grad_year` by running `readRosterClass` and keeping only `.gradYear`,
   * which is lossy in exactly the direction that matters: once the cell is a four-digit year the
   * class it named is gone, and with it the JC year. The review step reads both ladders off this
   * text, so it has to survive the flattening rather than being reconstructed from a number that
   * cannot carry it.
   */
  grad_year: string;
  school: string;
  email: string;
  notes: string;
  // Unpacked from stats_csv
  height: string;
  weight: string;
  gpa: string;
  // Unpacked from contact_csv
  phone: string;
  twitter: string;
  instagram: string;
  address: string;
  city: string;
  state: string;
}

/**
 * Expand player row by unpacking stats_csv and contact_csv
 */
function expandPlayerRow(player: ExtractedPlayerRow): ExpandedPlayerData {
  const stats = parseSemicolonCsv(player.stats_csv);
  const contact = parseSemicolonCsv(player.contact_csv);

  return {
    roster_type: player.roster_type,
    team_id: player.team_id,
    team_color: player.team_color,
    number: player.number,
    name_last: player.name_last,
    name_first: player.name_first,
    position: player.position,
    bats: player.bats,
    throws: player.throws,
    // The raw cell, not player.grad_year. See the note on the field above: the normalized year
    // cannot express a class, so a JUCO roster arrived at the review step with every row
    // unanswered and the coach filling in by hand what the parser had already read.
    grad_year: player.class,
    school: player.school,
    email: player.email,
    notes: player.notes || '-',
    // Unpacked from stats_csv
    height: stats.ht || '-',
    weight: stats.wt || '-',
    gpa: stats.gpa || '-',
    // Unpacked from contact_csv
    phone: contact.phone || '-',
    twitter: contact.twitter || '-',
    instagram: contact.instagram || '-',
    address: contact.address || '-',
    city: contact.city || contact.hometown || '-',
    state: contact.state || '-',
  };
}

/**
 * Check if a field has any non-empty values across all expanded players
 */
function expandedFieldHasData(players: ExpandedPlayerData[], fieldName: keyof ExpandedPlayerData): boolean {
  return players.some(player => {
    const value = player[fieldName];
    return value != null && value !== '' && value !== '-';
  });
}

/**
 * Build ParsedCSV from extracted player rows
 * Only includes columns that actually have data
 * Unpacks stats_csv and contact_csv into separate fields
 */
function buildParsedCSV(
  players: ExtractedPlayerRow[],
  rosterType: 'single_team' | 'showcase'
): ParsedCSV {
  if (players.length === 0) {
    throw new Error('No players extracted from response');
  }

  // Expand all players to unpack stats_csv and contact_csv
  const expandedPlayers = players.map(expandPlayerRow);

  // Define all possible fields with their header names and player field mappings
  type FieldDef = { header: string; field: keyof ExpandedPlayerData };

  const showcaseFields: FieldDef[] = [
    { header: 'team_id', field: 'team_id' },
    { header: 'team_color', field: 'team_color' },
  ];

  const playerFields: FieldDef[] = [
    { header: 'number', field: 'number' },
    { header: 'last_name', field: 'name_last' },
    { header: 'first_name', field: 'name_first' },
    { header: 'position', field: 'position' },
    { header: 'bats', field: 'bats' },
    { header: 'throws', field: 'throws' },
    { header: 'grad_year', field: 'grad_year' },
    { header: 'school', field: 'school' },
    { header: 'email', field: 'email' },
    { header: 'notes', field: 'notes' },
    // Unpacked from stats_csv
    { header: 'height', field: 'height' },
    { header: 'weight', field: 'weight' },
    // Unpacked from contact_csv
    { header: 'phone', field: 'phone' },
    { header: 'twitter', field: 'twitter' },
    { header: 'instagram', field: 'instagram' },
    { header: 'address', field: 'address' },
    { header: 'city', field: 'city' },
    { header: 'state', field: 'state' },
  ];

  // Always include name fields, filter others based on whether they have data
  const requiredFields: (keyof ExpandedPlayerData)[] = ['name_last', 'name_first'];

  // Build list of fields to include (only those with data, plus required fields)
  const fieldsToInclude: FieldDef[] = [];

  if (rosterType === 'showcase') {
    for (const f of showcaseFields) {
      if (expandedFieldHasData(expandedPlayers, f.field)) {
        fieldsToInclude.push(f);
      }
    }
  }

  for (const f of playerFields) {
    if (requiredFields.includes(f.field) || expandedFieldHasData(expandedPlayers, f.field)) {
      fieldsToInclude.push(f);
    }
  }

  // Build CSV headers
  const csvHeaders = fieldsToInclude.map(f => f.header);

  // Build rows directly (no CSV serialization round-trip)
  const rows: ExtractedRow[] = expandedPlayers.map(player => {
    const row: ExtractedRow = {};
    for (const f of fieldsToInclude) {
      const value = player[f.field];
      row[f.header] = (value === '-' || value == null) ? '' : value;
    }
    return row;
  });

  return {
    headers: csvHeaders,
    rows,
    rowCount: rows.length,
    columnCount: csvHeaders.length,
    rosterType,
  };
}
